import React, { Component } from 'react';
import {
	BrowserRouter as Router,
	Route,
	Switch
} from 'react-router-dom';
import LoginPage from './Components/LoginPage.js';
import ForgotPassword from './Components/ForgotPassword';
import PasswordReset from './Components/PasswordReset';
import LandingPage from './Components/LandingPage.js';
import MasterPool from './Components/MasterPool.js';
import NewPubPage from './Components/NewPubPage.js';
import EditPubPage from './Components/EditPubPage.js';
import SitePreview from './Components/SitePreview.js';
import OrganizationSetup from './Components/OrganizationSetup.js';
import UserSetup from './Components/UserSetup.js';
import UserPrefs from './Components/UserPrefs.js';
import { getCurrentUser } from './Utils/AuthUtils';
import './App.css';

// Base component for the entire application
class App extends Component {
	constructor(props) {
		super(props)
		this.state = {
			isAuthenticated: false,
			currentUser: null,
			isLoading: false,
			userData: {},
			activeOrg: 0,
			orgList: {}
		};

		this.onloginClick = this.onloginClick.bind(this);
		this.loadCurrentUser = this.loadCurrentUser.bind(this)
		this.setSessionStorage = this.setSessionStorage.bind(this);
		this.getSessionStorage = this.getSessionStorage.bind(this);
		this.logoutFn = this.logoutFn.bind(this);
		this.orgChangeFn = this.orgChangeFn.bind(this);
		this.getOrgList = this.getOrgList.bind(this);
		this.forceUserUpdateFn = this.forceUserUpdateFn.bind(this);
	}

	loadCurrentUser() {
		this.setState({
			isLoading: true
		});
		getCurrentUser()
			.then(response => {
				this.setState({
					currentUser: response,
					isAuthenticated: true,
					isLoading: false
				});
			}).catch(error => {
			this.setState({
				isLoading: false
			});
		});
	}

	// Set user as logged in. Should be passed information from the LoginPage component
	onloginClick(signIn, data) {
		data.storageTimeout = Date.now();
		const activeOrg = data.startOrg ? data.startOrg : 0;
		this.setState({ isAuthenticated: signIn, userData: data, activeOrg: activeOrg });

		if (signIn) { // Store that data in the session, if possible
			clearData();
			this.setSessionStorage('userData', data);
		}
		else { // Clear stored login data on authentication failures
			if (sessionStorage) {
				sessionStorage.clear();
				clearData();
			}
		}

	}
	
	// Triggered by a lower component to tell the interface to update userData, because something is different
	forceUserUpdateFn() {
		fetch('/webpub-services/WPUserAPI/getWPUserById/' + this.state.userData.id)
		.then(res => res.json())
		.then(
			result => {
				this.setState({
					userData: result[0]
				});
				this.setSessionStorage('userData', result[0]);
			}
		);
	}

	// Create a local session stash for this data to prevent logout on refresh
	setSessionStorage(dataKey, data) {
		if (!sessionStorage) {
			// No use trying to use session storage if this browser doesn't have it!
			return;
		}

		if (data) {
			sessionStorage.setItem(dataKey, JSON.stringify(data));
		}
		else {
			sessionStorage.clear();
		}
	}

	// Retrieve local session variables
	getSessionStorage() {
		if (!sessionStorage) {
			// No use trying to use session storage if this browser doesn't have it!
			return false;
		}

		const myDataObj = sessionStorage.getItem('userData');
		if (myDataObj) { // Something was in there!
			const JSONDataObj = JSON.parse(myDataObj);

			const storageTimeout = JSONDataObj.storageTimeout ? JSONDataObj.storageTimeout : 0;

			// 7200000 ms = 2 hours for this session to time out
			if (storageTimeout && (Date.now() > (parseInt(storageTimeout) + 7200000))) {
				sessionStorage.clear();
				clearData();
			}
			else {
				this.setState({
					isAuthenticated: true,
					userData: JSONDataObj,
					activeOrg: JSONDataObj.startOrg ? JSONDataObj.startOrg : 0
				});
			}
		}
	}

	// Wipe authentication and user data
	logoutFn() {
		this.setState({
			isAuthenticated: false,
			userData: {},
			activeOrg: 0
		}, this.setSessionStorage('userData', false));
		clearData();
	}

	// Change the user's active Organization
	// if setDefault is true, this will also set the newOrg to their default startOrg
	orgChangeFn(newOrg, setDefault=false) {
		if (setDefault === "true") {
			if (newOrg === this.state.userData.startOrg) { // Go home, user. You're drunk.
				this.setState({activeOrg: newOrg});
				return;
			}
			var newUserData = this.state.userData;
			newUserData.startOrg = newOrg;
			newUserData.modifiedUser = newUserData.id;
			newUserData.modifiedDate = Date.now();

			fetch('/webpub-services/WPUserAPI/updateWPUser', {
				method: 'PUT',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(newUserData)
			})
				.then(res => res.json())
				.then(
					(result) => {
						if (result.length && result[0].active) {
							this.setState({userData : result[0]}, () => {
								this.setSessionStorage('userData', this.state.userData);
							});
						}
					},

					(error) => {
						// TODO: Do something if this fails, maybe?
					}
				);
		}

		this.setState({activeOrg: newOrg});
	}

	// Populate the list of organizations from the server
	getOrgList() {
		fetch('/webpub-services/organizationAPI/getAllOrganizations',
			{
				method: 'GET',
			}
		)
			.then(res => res.json())
			.then(
				(result) => {
					if (result.length && result[0].name) { // Results look like we expect
						this.setState({orgList : result}, () => {
							this.setSessionStorage('orgList', this.state.orgList);
						});
					}
				},

				(error) => {
					// TODO: Do something if this fails, maybe?
				}
			);
	}

	// Initial page view
	componentDidMount() {
		if (!this.state.isAuthenticated) {
			this.getSessionStorage();
		}
		this.getOrgList();
	}

	render() {
		return (
			<Router>
				<Switch>
					<Route exact path="/(login|)"
						   component = {
							   (props) =>
								   <LoginPage
									   {...props}
									   isAuthenticated = {this.state.isAuthenticated}
									   onSigninHandle = {this.onloginClick}
								   />
						   }
					/>
					<Route path="/forgotpassword" component={ForgotPassword} />
					<Route path='/passwordreset' component={PasswordReset} />
					{ this.state.isAuthenticated
						? <Route component = {
							(props) => <DefaultContainer
								userData={this.state.userData}
								orgList={this.state.orgList}
								logoutFn={this.logoutFn}
								orgChangeFn={this.orgChangeFn}
								activeOrg={this.state.activeOrg}
								forceUserUpdateFn={this.forceUserUpdateFn}
							/>
						}
						/>
						: <Route component = {
							(props) =>
								<LoginPage
									{...props}
									isAuthenticated = {this.state.isAuthenticated}
									onSigninHandle = {this.onloginClick}
								/>
						}
						/>

					}
				</Switch>
			</Router>
		);
	}
}

export default App;

// DefaultContainer handles behind-the-scenes routing. For top-level buttons, see the LandingPage component.
const DefaultContainer = (data) => {
	return (
		<div className="wrap-bcg app-container-landing-page">
			<LandingPage
				userData={data.userData}
				logoutFn={data.logoutFn}
				orgChangeFn={data.orgChangeFn}
				activeOrg={data.activeOrg}
				orgList={data.orgList}
			/>
			<Switch>
				<Route
					path="/masterpool"
					component={(props) => <MasterPool {...props}
													  userData={data.userData}
													  activeOrg={data.activeOrg}
													  orgList={data.orgList}
					/>}
				/>
				<Route
					path="/new"
					component={(props) => <NewPubPage {...props}
													  userData={data.userData}
													  activeOrg={data.activeOrg}
					/>}
				/>
				<Route
					path="/edit"
					component={(props) => <EditPubPage {...props}
													   userData={data.userData}
													   activeOrg={data.activeOrg}
					/>}
				/>
				<Route
					path="/preview"
					component={(props) => <SitePreview {...props}
													   userData={data.userData}
													   activeOrg={data.activeOrg}
					/>}
				/>
				<Route
					path="/organizations"
					component={(props) => <OrganizationSetup {...props}
                                                             orgList={data.orgList}
															 userData={data.userData}
															 activeOrg={data.activeOrg}
					/>}
				/>
				<Route
					path="/users"
					component={(props) => <UserSetup {...props}
													 userData={data.userData}
													 activeOrg={data.activeOrg}
					/>}
				/>
				<Route
					path="/prefs"
					component={(props) => <UserPrefs {...props}
													 userData={data.userData}
													 activeOrg={data.activeOrg}
													 orgList={data.orgList}
													 orgChangeFn={data.orgChangeFn}
													 forceUserUpdateFn={data.forceUserUpdateFn}
					/>}
				/>
				<Route component={PageNotFound} />
			</Switch>
			<Footer />
		</div>
	);
}

// If all goes well, we will never see this:
class PageNotFound extends React.Component {
	render() {
		return (
			<div>
				<br />
				<h2>File Not Found</h2>
				<p>
					We're sorry, but the page you were trying to view could not be found.
					Please follow one of the links above to return to the application.
				</p>
				<p>
					If you believe you have reached this page in error, please contact your administrator.
				</p>
			</div>
		);
	}
};
// Copyright info that goes under every page
class Footer extends React.Component {
	render() {
		return (
			<div className='app-footer'>© {new Date().getFullYear()} University of Kansas</div>
		);
	}
}
// Clear out localStorage if applicable
// TODO: This will need to be in an importable file if we want to clear localStorage elsewhere in the application
const clearData = () => {
	try {
		localStorage.clear();
	}
	catch(e){
		//do nothing
	}
};